import { useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { processBookingExcel, importMonthlyData, ImportPreview } from '../../services/bookingImport';
import toast from 'react-hot-toast';
import { BookingImportPreview } from './BookingImportPreview';

interface BookingExcelImporterProps {
  className?: string;
}

export const BookingExcelImporter = ({ className }: BookingExcelImporterProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [importing, setImporting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null); // Limpiar preview anterior
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setLoading(true);
    try {
      const previewData = await processBookingExcel(file, year);
      setPreview(previewData);
      
      if (previewData.warnings.length > 0) {
        toast.error(
          `Se encontraron ${previewData.warnings.length} advertencias. Revisa el preview.`,
          { duration: 5000 }
        );
      } else {
        toast.success('Archivo analizado correctamente');
      }
    } catch (error: any) {
      console.error('Error analizando archivo:', error);
      toast.error(error.message || 'Error al analizar el archivo');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (conflictResolution: 'skip' | 'update' | 'sum') => {
    if (!preview) return;

    // Confirmar antes de importar
    const confirmMessage = 
      conflictResolution === 'skip'
        ? '¿Importar solo los registros nuevos (omitir conflictos)?'
        : conflictResolution === 'update'
        ? '¿Actualizar los registros existentes con los nuevos valores?'
        : '¿Sumar los nuevos valores a los existentes?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setImporting(true);
    try {
      const allMonthlyData = preview.propertySheets.flatMap(ps => ps.monthlyData);
      const result = await importMonthlyData(allMonthlyData, conflictResolution);

      if (result.errors.length > 0) {
        toast.error(
          `Importación completada con errores:\n${result.success} exitosos, ${result.skipped} omitidos, ${result.errors.length} errores`,
          { duration: 8000 }
        );
        console.error('Errores de importación:', result.errors);
      } else {
        toast.success(
          `Importación exitosa: ${result.success} registros importados, ${result.skipped} omitidos`
        );
        
        // Limpiar después de importar
        setFile(null);
        setPreview(null);
        
        // Reset file input
        const fileInput = document.getElementById('booking-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
    } catch (error: any) {
      console.error('Error importando datos:', error);
      toast.error(error.message || 'Error al importar los datos');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    const fileInput = document.getElementById('booking-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <Card className={className}>
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-2">Importar desde Libro de Reservas</h2>
        <p className="text-gray-600 mb-6">
          Importa datos mensuales desde tu archivo Excel de reservas. El sistema agregará automáticamente
          las reservas individuales por mes y propiedad.
        </p>

        {/* Selector de año */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Año de las reservas
          </label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            min="2020"
            max="2030"
            className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Selector de archivo */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Archivo Excel de Reservas
          </label>
          <div className="flex items-center space-x-4">
            <input
              id="booking-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                cursor-pointer"
            />
            {file && (
              <Button
                onClick={handleReset}
                variant="secondary"
                size="sm"
              >
                Limpiar
              </Button>
            )}
          </div>
          {file && (
            <p className="mt-2 text-sm text-gray-600">
              Archivo seleccionado: <span className="font-medium">{file.name}</span>
            </p>
          )}
        </div>

        {/* Botón de análisis */}
        {file && !preview && (
          <div className="mb-6">
            <Button
              onClick={handleAnalyze}
              loading={loading}
              variant="primary"
              className="w-full sm:w-auto"
            >
              Analizar Archivo
            </Button>
            <p className="mt-2 text-sm text-gray-500">
              El análisis procesará todas las pestañas de propiedades y mostrará un preview
              de los datos antes de importar.
            </p>
          </div>
        )}

        {/* Preview de datos */}
        {preview && (
          <BookingImportPreview
            preview={preview}
            onImport={handleImport}
            onCancel={handleReset}
            importing={importing}
          />
        )}

        {/* Información adicional */}
        {!preview && (
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              ℹ️ Información importante
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• El archivo debe ser el Excel de "Libro de Reservas" con las pestañas de propiedades</li>
              <li>• Se procesarán automáticamente las 36 pestañas de propiedades</li>
              <li>• Las reservas se agruparán por mes y se calcularán los totales</li>
              <li>• Se detectarán conflictos con datos existentes en la base de datos</li>
              <li>• Podrás revisar todos los datos antes de confirmar la importación</li>
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
};
